import type { DriveType } from "../enums";
import type { CostType } from "./expenses";

/**
 * Fill-ups: a fuel or charging expense may say how much went in, in milli-units of its unit.
 * Which units are offered follows the vehicle's drive type; the server accepts any unit that fits
 * the cost type, so changing a vehicle's drive type never makes an old fill-up invalid.
 */

export const FUEL_UNITS = ["l", "gal_us", "gal_uk", "kg", "kwh"] as const;
export type FuelUnit = (typeof FUEL_UNITS)[number];

/** Decimals a quantity is entered with, as on the pump. */
export const FUEL_UNIT_DECIMALS: Readonly<Record<FuelUnit, number>> = {
  l: 2,
  gal_us: 2,
  gal_uk: 2,
  kg: 2,
  kwh: 2,
};

export type FillUpType = Extract<CostType, "fuel" | "charging">;

export function isFillUpType(costType: CostType): costType is FillUpType {
  return costType === "fuel" || costType === "charging";
}

const LIQUID: readonly FuelUnit[] = ["l", "gal_us", "gal_uk"];

/** The units a fill-up of this cost type may be in. */
export function unitsFor(costType: FillUpType): readonly FuelUnit[] {
  return costType === "charging" ? ["kwh"] : [...LIQUID, "kg"];
}

export interface FillUpOptions {
  /** Units offered for fuel, the first the default; empty where the vehicle takes none. */
  fuel: readonly FuelUnit[];
  /** Whether the vehicle is charged (kWh). */
  charging: boolean;
}

/**
 * What the expense sheet offers for a vehicle: an LPG or CNG car also runs on petrol, a plug-in
 * hybrid takes fuel and charging, an electric car only charging. Without a drive type (or a
 * vehicle), everything.
 */
export function fillUpOptions(driveType: DriveType | null): FillUpOptions {
  switch (driveType) {
    case "petrol":
    case "diesel":
    case "lpg":
      return { fuel: LIQUID, charging: false };
    case "cng":
      return { fuel: ["kg", ...LIQUID], charging: false };
    case "phev":
      return { fuel: LIQUID, charging: true };
    case "bev":
      return { fuel: [], charging: true };
    case null:
      return { fuel: [...LIQUID, "kg"], charging: true };
  }
}

/**
 * What one unit cost, in thousandths of a minor unit, so a price per litre keeps its third
 * decimal (1,789 €/l is 178 900). Null without a quantity.
 */
export function pricePerUnit(amountMinor: number, qty: number | null): number | null {
  if (!qty || qty <= 0) return null;
  return Math.round((amountMinor * 1_000_000) / qty);
}
